import { Children, cloneElement, isValidElement, ReactElement, ReactNode, useCallback, useId, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useClickAway, useKey } from 'react-use'

import { PORTAL_CLASS_NAME } from '../../constants/portalClassName'
import { DefaultValue } from '../../enums/defaultValue'
import { useComponentToggle } from '../../hooks/useComponentToggle'
import { isTopmostPortalElement } from '../../utils/isTopmostPortalElement'
import { transitionToMiliseconds } from '../../utils/transitionToMiliseconds'
import { useDrawer } from './context/drawerContext'
import { DrawerSqueezeZone } from './DrawerSqueezeZone'
import { DrawerBody, DrawerFooter, DrawerHeader, DrawerStep, DrawerSteps } from './elements'
import { usePushContentOffset } from './hooks/usePushContentOffset'
import * as Styled from './styles'
import { Mode } from './types/mode'

export interface DrawerProps {
  children?: ReactNode
  closable?: boolean
  /**
   * Close the drawer when the user clicks outside of it.
   * Defaults to `true` for the legacy `Opened` mode and `false` for `Compact`/`Expanded`,
   * which are designed to coexist with the main content column.
   */
  closeOnClickAway?: boolean
  /**
   * Show the expand/collapse toggle in `Drawer.Header`. Only meaningful in
   * `Compact`/`Expanded` modes. Defaults to `false`.
   */
  expandable?: boolean
  id?: string
  labelId?: string
  /**
   * DOM node to portal into instead of `document.body` — e.g. so the drawer renders confined
   * within a fixed-size container rather than the full viewport. Defaults to `document.body`.
   */
  portalContainer?: Element
}

type DrawerChildProps = {
  closable?: boolean
  expandable?: boolean
  labelId?: string
}

export const Drawer = ({
  children,
  closable = true,
  closeOnClickAway,
  expandable = false,
  id: idProp,
  portalContainer = document.body,
}: DrawerProps): ReactElement => {
  const generatedId = useId()
  const labelId = idProp ?? generatedId

  const wrapperRef = useRef<HTMLDivElement>(null)
  const { mode, isOpen, requestClose } = useDrawer()
  const { isVisible, isRendered } = useComponentToggle({
    isOpen,
    animationTime: transitionToMiliseconds(DefaultValue.TransitionDuration),
  })

  usePushContentOffset({ isCompact: mode === Mode.Compact })

  const isFlexibleMode = mode === Mode.Compact || mode === Mode.Expanded
  const shouldCloseOnClickAway = closeOnClickAway ?? !isFlexibleMode

  const handleClickAway = useCallback(
    (event: Event) => {
      if (!closable || !shouldCloseOnClickAway) {
        return
      }
      const target = event.target as HTMLElement
      if (target.closest(`.${PORTAL_CLASS_NAME}`)) {
        return
      }
      requestClose()
    },
    [closable, shouldCloseOnClickAway, requestClose],
  )

  const handleEscapePress = useCallback(() => {
    if (closable && isTopmostPortalElement(wrapperRef?.current)) {
      requestClose()
    }
  }, [closable, requestClose])

  useClickAway(wrapperRef, handleClickAway)
  useKey('Escape', handleEscapePress)

  return ReactDOM.createPortal(
    <>
      {isRendered && (
        <Styled.DrawerWrapper
          ref={wrapperRef}
          isOpen={isVisible}
          mode={mode}
          className={PORTAL_CLASS_NAME}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelId}
        >
          {Children.map(children, (child) => {
            if (isValidElement<DrawerChildProps>(child)) {
              return cloneElement(child, { closable, expandable, labelId })
            }
            return child
          })}
        </Styled.DrawerWrapper>
      )}
    </>,
    portalContainer,
  )
}

Drawer.Header = DrawerHeader
/* Steps is a wrapper component managing multiple Step-s. It includes Drawer.Step as a child component and a Header which would be including Step-specific header */
Drawer.Steps = DrawerSteps
Drawer.Step = DrawerStep
/* Body is an actual styled body of the drawer/step. All content of the drawer/step should be placed inside the Body component, inside Step (<Steps><Step><Body>{content}</Body></Step></Steps>) or not (<Drawer><Body>{content}</Body></Drawer>). */
Drawer.Body = DrawerBody
Drawer.Footer = DrawerFooter
Drawer.SqueezeZone = DrawerSqueezeZone
